// The folders a machine's documents sit in (round 31).
//
// The web half of `server/src/lib/attachmentFolders.ts`, and the sibling of
// `personSections.ts`: ONE list, and the headings, the empty lines, the upload
// picker and the order they draw all read from it. Written out separately they
// drift, and the way you find out is a customer asking why the Warranty
// heading lists a rego.
//
// A machine's page is NOT tabbed the way a person's is, and that is the whole
// design decision here. A person's page is mostly documents and records — the
// tabs ARE the page. A machine's page is meters, service history, prestart
// history and availability, with documents at the bottom; seven tabs across
// the top would hide the meters behind a tab called Overview, which is a worse
// page in exchange for a consistency nobody asked for. So the folders are
// GROUPS inside the one documents panel, and an empty one draws nothing.
import type { AttachmentCategory } from '../documentsApi';

export interface DocumentFolder {
  key: AttachmentCategory;
  label: string;
  /** The line under the upload box while this folder is selected. */
  hint: string;
}

/**
 * Plant and vehicles, in the order they draw: the paperwork somebody goes
 * looking for first, then the two catch-alls.
 */
export const ASSET_FOLDERS: readonly DocumentFolder[] = [
  {
    key: 'permits_registrations',
    label: 'Permits & registrations',
    hint: 'Registration papers, permits, anything with an authority’s name on it. Give it an expiry and it will be chased.',
  },
  {
    key: 'manuals',
    label: 'Manuals',
    hint: 'Operator and workshop manuals, parts books, wiring diagrams.',
  },
  {
    key: 'risk_assessments',
    label: 'Risk assessments',
    hint: 'Plant risk assessments and safe work method statements for this machine.',
  },
  {
    key: 'service_invoices',
    label: 'Service & repair invoices',
    hint: 'Invoices for work done. Attach one to a service record and it shows in both places.',
  },
  {
    key: 'warranty',
    label: 'Warranty',
    hint: 'Warranty statements, claims and their correspondence.',
  },
  {
    key: 'photos',
    label: 'Photos',
    hint: 'Photos of the machine: condition, damage, the plate. They open in the app.',
  },
  {
    key: 'miscellaneous',
    label: 'Miscellaneous',
    hint: 'Anything that does not belong in the folders above.',
  },
];

/**
 * Which folder a document draws under.
 *
 * Null reads as Miscellaneous — that is what everything uploaded before
 * folders existed looks like, and it needs somewhere to appear rather than
 * nowhere. The same rule `personSections.folderOf` states for people.
 */
export function folderOf(category: AttachmentCategory | null): AttachmentCategory {
  return category ?? 'miscellaneous';
}
